import { ApiResponse } from "types/api.types";
import axiosClient from "utils/axios";
import type { AppSettingsJson } from "../types/settings.types";
import {
  normalizeBankRow,
  normalizeCompanyRow,
  normalizeSmtpRow,
} from "../utils/settingsNormalize";
import type {
  BankDetailRow,
  CompanyProfileRow,
  SmtpSettingRow,
} from "../types/settings.types";

const SETTINGS_ROOT = "/settings";

function unwrapPayload<T>(res: ApiResponse<T>): T | null {
  if (!res.success) return null;
  return (res.data ?? null) as T | null;
}

/** Global `app_settings` JSON (shallow merge on PUT). */
export const getAppSettings = async () => {
  const { data } = await axiosClient.get<ApiResponse<AppSettingsJson>>(
    SETTINGS_ROOT,
  );
  return data;
};

export const updateAppSettings = async (patch: Record<string, unknown>) => {
  const { data } = await axiosClient.put<ApiResponse<AppSettingsJson>>(
    SETTINGS_ROOT,
    patch,
  );
  return data;
};

export const getCompany = async (): Promise<CompanyProfileRow | null> => {
  const { data } = await axiosClient.get<ApiResponse<unknown>>(
    `${SETTINGS_ROOT}/company`,
  );
  const raw = unwrapPayload<unknown>(data);
  return normalizeCompanyRow(raw);
};

export type CompanyUpdateBody = {
  name: string;
  address: string;
  phone: string;
  email: string;
  gstNumber: string;
};

export const updateCompany = async (body: CompanyUpdateBody) => {
  const { data } = await axiosClient.put<ApiResponse<unknown>>(
    `${SETTINGS_ROOT}/company`,
    body,
  );
  return data;
};

export const getBank = async (): Promise<BankDetailRow | null> => {
  const { data } = await axiosClient.get<ApiResponse<unknown>>(
    `${SETTINGS_ROOT}/bank`,
  );
  const raw = unwrapPayload<unknown>(data);
  return normalizeBankRow(raw);
};

export type BankUpdateBody = {
  accountName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  branch: string;
};

export const updateBank = async (body: BankUpdateBody) => {
  const { data } = await axiosClient.put<ApiResponse<unknown>>(
    `${SETTINGS_ROOT}/bank`,
    body,
  );
  return data;
};

export const getSmtp = async (): Promise<SmtpSettingRow | null> => {
  const { data } = await axiosClient.get<ApiResponse<unknown>>(
    `${SETTINGS_ROOT}/smtp`,
  );
  const raw = unwrapPayload<unknown>(data);
  return normalizeSmtpRow(raw);
};

export type SmtpUpdateBody = {
  host: string;
  fromEmail: string;
  port?: number;
  secure?: boolean;
  username?: string | null;
  fromName?: string | null;
  /** Omit to leave unchanged; `null` clears stored password. */
  password?: string | null;
};

export const updateSmtp = async (body: SmtpUpdateBody) => {
  const { data } = await axiosClient.put<ApiResponse<unknown>>(
    `${SETTINGS_ROOT}/smtp`,
    body,
  );
  return data;
};

/** @deprecated Use `getAppSettings` — legacy name. */
export const getSettings = getAppSettings;

/** @deprecated Use `updateAppSettings` — was POST; API now uses PUT merge. */
export const saveSettings = updateAppSettings;

const SettingsService = {
  getAppSettings,
  updateAppSettings,
  getSettings,
  saveSettings,
  getCompany,
  updateCompany,
  getBank,
  updateBank,
  getSmtp,
  updateSmtp,
};

export default SettingsService;
